import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import { useAuthRouter } from '../utils/AuthRouter';
import { getEtid, persistEtid } from '../shared/etid';
import {
  clearPartnerCode,
  normalizePartnerCode,
  persistPartnerCode,
} from '../shared/partnerCode';
import { safeGetItem, safeSessionGetItem } from '../shared/safeStorage';
import {
  getSelfReportedEmailCookie,
  isValidEmail,
  persistSelfReportedEmail,
  SELF_REPORTED_EMAIL_STORAGE_KEY,
} from '../shared/selfReportedEmail';

function Login() {
  const location = useLocation();
  const { loginWithRedirect, handleRedirectCallback } = useAuth();
  const { isLoading, isAuthenticated } = useAuthRouter();

  useEffect(() => {
    const handleAuth = async () => {
      if (isLoading) return;

      // Co-branded application link (tachusa.com/apply/<partner>). Read before
      // the authenticated early return below: a visitor with a live Auth0
      // session but no customer record yet still needs the code kept for
      // RoleSelect. Only a well-formed code is kept; anything else is ignored
      // without error so a bad link still logs the visitor in normally.
      // Persisted so it survives an Auth0 silent-renew or refresh, and carried
      // in appState so it survives the Auth0 round-trip (restored in
      // index.tsx).
      const urlParams = new URLSearchParams(location.search);
      const partner = normalizePartnerCode(urlParams.get('partner'));
      if (partner) {
        persistPartnerCode(partner);
      } else if (urlParams.get('from') === 'signup') {
        // A signup link with no partner is an unaffiliated carrier: drop a
        // code left behind by an earlier partner link that was never finished,
        // so it cannot be attributed to this signup. Not done for other
        // /login visits (Auth0 returns, re-logins), which never carry one.
        clearPartnerCode();
      }

      // Already authenticated: let AuthRouter redirect to the dashboard/role.
      // Calling loginWithRedirect here would bounce a logged-in user through
      // Auth0's live SSO session back to /login; onRedirectCallback then strips
      // the params and re-fires this effect → infinite login loop.
      if (isAuthenticated) return;

      // Check if we're in the callback URL
      const hasAuthParams = location.search.includes('state=');

      if (hasAuthParams) {
        return;
      }

      // If not authenticated and no auth params, initiate login
      if (!hasAuthParams) {
        try {
          const isFromSignup = urlParams.get('from') === 'signup';

          // Get the redirect path from URL param, fallback to sessionStorage
          const returnToParam = urlParams.get('returnTo');
          const redirectPath =
            returnToParam || safeSessionGetItem('redirectPath');

          // Get tachref from localStorage (stored by AuthRouter)
          const tachref = safeGetItem('tachref');

          // Get tachRole from URL param
          const tachRole = urlParams.get('tachRole');

          // Get etid from the URL param, then fall back to the cross-domain
          // `.tachusa.com` cookie set by the marketing site, then localStorage.
          // This recovers attribution when a user reaches app.tachusa.com
          // without ?etid= in the URL (e.g. mobile / direct entry).
          const urlEtid = urlParams.get('etid');

          // Persist a URL-provided ETID so attribution survives an Auth0
          // silent-renew, refresh, or back-navigation without the param.
          if (urlEtid) persistEtid(urlEtid);

          const etid = getEtid(urlEtid);

          // Self-reported email captured in the landing-page pre-prequal flow,
          // reusing the etid URL→cookie→localStorage precedence. Persist a
          // URL-provided email so the prefill survives an Auth0 silent-renew,
          // refresh, or back-navigation without the param.
          const urlEmail = urlParams.get('email');
          if (urlEmail) persistSelfReportedEmail(urlEmail);

          // Gate the raw URL value through isValidEmail so a malformed
          // ?email= never reaches login_hint. The cookie/localStorage
          // fallbacks were validated on write by persistSelfReportedEmail,
          // so only the URL param needs checking here.
          const loginHintEmail =
            (urlEmail && isValidEmail(urlEmail) ? urlEmail.trim() : null) ||
            getSelfReportedEmailCookie() ||
            safeGetItem(SELF_REPORTED_EMAIL_STORAGE_KEY);

          // Store redirect path, tachref, tachRole, etid, and partner in appState
          const appState: any = {};
          if (redirectPath) appState.returnTo = redirectPath;
          if (tachref) appState.tachref = tachref;
          if (tachRole) appState.tachRole = tachRole;
          if (etid) appState.etid = etid;
          if (partner) appState.partner = partner;

          await loginWithRedirect({
            authorizationParams: {
              screen_hint: isFromSignup ? 'signup' : 'login',
              scope: 'openid profile email',
              // Prefill the Auth0 signup box only on the signup path. Login
              // behavior is unchanged for returning (non-signup) users.
              //
              // NOTE: sending login_hint does NOT visibly prefill the signup
              // screen while the tenant uses the "Identifier First"
              // authentication profile — Auth0 ignores login_hint there by
              // design. An empty box is therefore EXPECTED until a tenant owner
              // switches Authentication Profile to "Identifier + Password" (see
              // spec Part B / B3). The request still carries login_hint so the
              // prefill works automatically once that tenant change lands.
              ...(loginHintEmail && isFromSignup
                ? { login_hint: loginHintEmail }
                : {}),
            },
            appState: Object.keys(appState).length > 0 ? appState : undefined,
          });
        } catch (error: any) {
          console.error('Error during login:', error?.message);
        }
      }
    };

    handleAuth();
  }, [
    isLoading,
    isAuthenticated,
    loginWithRedirect,
    handleRedirectCallback,
    location,
  ]);

  return (
    <div className="z-40 flex h-[100vh] w-[100vw] flex-col items-center justify-center align-middle">
      <img
        alt="loading-icon"
        src="/Icon-v2.gif"
        className="flex h-44 w-44 justify-center"
      />
      <h2 className="text-[14px] text-secondary">Logging in...</h2>
    </div>
  );
}

export default Login;
